import { Children, cloneElement, isValidElement, type ReactNode, useMemo } from "react";
import type { Components, Options } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { CitationChip } from "@/components/citations/InlineFootnotes";
import { SafeImage } from "@/components/thread/SafeImage";
import { SyntaxCodeBlock } from "@/components/thread/SyntaxCodeBlock";
import { codePointToUtf16 } from "@/runtime/attributionOffsets";
import {
  buildSourceNumberByChunkId,
  type CitationCohort,
  classifyCitationCohort,
  isAttributionCitation,
} from "@/runtime/citationDiscriminator";
import { type AttributionSpan, rehypeAttributionChips } from "@/runtime/rehypeAttributionChips";
import { rehypeUnverifiedMarks } from "@/runtime/rehypeUnverifiedMarks";
import { buildUnverifiedSpans, insertClaimSentinels, type UnverifiedSpan } from "@/runtime/unverifiedMarks";
import type { PersistedCitationRef, UnverifiedClaimRef } from "@/types";

// Narrow cast target for the rehype attacher: react-markdown's own
// `Pluggable` (NOT `any`), bridging rehypeAttributionChips' local `Root`
// vs hast `Root`. `unified` is not hoisted, so derive from `Options`.
type RehypePluggable = NonNullable<Options["rehypePlugins"]>[number];

const CITATION_PATTERN = /\[(\d+)\]/g;

function extractLangFromClassName(className: unknown): string | undefined {
  if (typeof className !== "string") return undefined;
  const match = /language-([\w+-]+)/.exec(className);
  return match?.[1];
}

function extractText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(extractText).join("");
  if (children && typeof children === "object" && "props" in children) {
    const node = children as { props?: { children?: ReactNode } };
    return extractText(node.props?.children);
  }
  return "";
}

function buildProcessChildren(
  cohort: CitationCohort,
  byMarkerIndex: Map<number, PersistedCitationRef>,
  citationsByIndex: Map<number, PersistedCitationRef[]>,
  canonicalIdxByChunkId: Map<string, number>,
) {
  let keyCounter = 0;
  // Closure-wide tracker for consecutive same-canonical chip dedupe.
  // Persists across every processText call within one MessageRenderer
  // render so collapse happens even when the chips sit in different
  // text nodes (table cells, paragraphs, list items). Backend may emit
  // the same `[N]` marker repeatedly for the same canonical chunk in
  // adjacent positions, producing visual clusters like `[5][5][5][5]`.
  // A chip with a DIFFERENT canonical resets the chain implicitly so
  // legitimate citation density (different-source citations) is preserved.
  // ADR-096 cohort BYPASSES this collapse — the model places markers at
  // claim boundaries deliberately via the report_citations_tool schema;
  // collapsing would discard structural information the model encoded.
  let lastRenderedCanonicalIdx: number | null = null;
  // Iter 14 F1 — per-index occurrence counter. nth text occurrence of
  // `[N]` resolves to the nth backend-emitted PersistedCitationRef sibling for
  // that index. Closure-wide so cross-text-node positions count
  // correctly (e.g. `[5]` in p1 + `[5]` in p2 = different occurrences).
  // Clamps to siblings[last] when text has more `[N]` than backend
  // emitted (defensive against marker-count mismatch). LEGACY-ONLY:
  // ADR-096 uses 1:1 byMarkerIndex lookup (no siblings, no occurrence
  // counter) because the report_citations_tool schema guarantees
  // exactly one PersistedCitationRef per marker_index.
  const indexOccurrence = new Map<number, number>();

  // ADR-096 Phase 3 — per-marker resolution branch. Returns the resolved
  // cite + display index, OR null if the `[N]` marker has no backing
  // PersistedCitationRef (triggers iter 18 silent-consume below).
  //
  // Per-cohort semantics:
  //   - "adr096": 1:1 byMarkerIndex.get(idx) — no canonical remap
  //     (marker_index IS the canonical index by construction), no
  //     sibling rotation (model emits one PersistedCitationRef per marker via
  //     report_citations_tool).
  //   - "legacy" / "mixed": iter 14 F1 sibling resolution + iter 15
  //     canonical-idx remap (preserved verbatim for ADR-082 wire shape).
  function resolveCite(idx: number): { cite: PersistedCitationRef; displayIdx: number } | null {
    if (cohort === "adr096") {
      const cite = byMarkerIndex.get(idx);
      if (!cite) return null;
      return { cite, displayIdx: idx };
    }
    const siblings = citationsByIndex.get(idx);
    if (!siblings || siblings.length === 0) return null;
    const occCount = indexOccurrence.get(idx) ?? 0;
    const cite = siblings[Math.min(occCount, siblings.length - 1)];
    indexOccurrence.set(idx, occCount + 1);
    const displayIdx = canonicalIdxByChunkId.get(cite.chunk_id) ?? idx;
    return { cite, displayIdx };
  }

  function processText(text: string): ReactNode[] {
    const result: ReactNode[] = [];
    let lastEnd = 0;
    for (const match of text.matchAll(CITATION_PATTERN)) {
      const idx = Number(match[1]);
      if (match.index === undefined) continue;
      const resolved = resolveCite(idx);
      if (!resolved) {
        // Iter 18 silent-consume (preserved across BOTH cohorts).
        // Push intervening text BEFORE the marker, then advance lastEnd
        // past the `[N]` so the literal marker doesn't leak as plain text
        // in the final result.push(text.slice(lastEnd)) tail. When backend
        // does not bind a particular marker to a chunk (legacy cite_align
        // unbound; ADR-096 model emits an out-of-range marker), the user
        // reads cleanly without dead `[13]` / `[20]` / `[14]` characters
        // embedded in the answer body.
        if (match.index > lastEnd) {
          result.push(text.slice(lastEnd, match.index));
        }
        lastEnd = match.index + match[0].length;
        continue;
      }
      const { cite, displayIdx } = resolved;
      // ADR-096 cohort skips consecutive-collapse (cohort-aware predicate
      // applied INSIDE processText so the closure-wide state stays sound
      // even when both cohorts coexist across messages within one session
      // — per-message classification means each render sees ONE cohort).
      if (cohort !== "adr096" && displayIdx === lastRenderedCanonicalIdx) {
        lastEnd = match.index + match[0].length;
        continue;
      }
      if (match.index > lastEnd) {
        result.push(text.slice(lastEnd, match.index));
      }
      result.push(<CitationChip key={`cite-${keyCounter++}`} idx={idx} cite={cite} displayIdx={displayIdx} />);
      lastEnd = match.index + match[0].length;
      lastRenderedCanonicalIdx = displayIdx;
    }
    if (lastEnd === 0) return [text];
    if (lastEnd < text.length) result.push(text.slice(lastEnd));
    return result;
  }

  function process(children: ReactNode): ReactNode {
    return Children.map(children, (child) => {
      if (typeof child === "string") {
        const parts = processText(child);
        return parts.length === 1 ? parts[0] : <>{parts}</>;
      }
      if (isValidElement(child)) {
        const props = child.props as { children?: ReactNode };
        if (props.children !== undefined) {
          return cloneElement(child as React.ReactElement<{ children?: ReactNode }>, {}, process(props.children));
        }
      }
      return child;
    });
  }

  // Iter 15 block-scoped reset. Operator empirical (2026-06-04 fresh
  // testing on chat-ui:afcb4f36) surfaced "citation pills sometimes still
  // missing on the answers" on single-source-dominant queries. Threads
  // "what's the core competences of Bekaert?" (11 cites all canonical=5,
  // 2 chips visible) and "how many countries does Bekaert operate?" (8
  // cites all canonical=11, 3 chips visible) traced to iter 11/12
  // lastRenderedCanonicalIdx persisting CLOSURE-WIDE across the entire
  // answer body. After the first chip renders (lastRen=N), every
  // subsequent marker mapping to canonical=N hits the SKIP path,
  // collapsing all per-paragraph claims into a single chip.
  //
  // Fix: blockProcess resets lastRenderedCanonicalIdx before processing
  // a block's children. Each <p> / <h1-h3> / <li> / <th> / <td> handler
  // calls blockProcess; inline handlers (<a>, <strong>, <em>) keep
  // plain process so adjacent same-canonical clusters `[5][5][5][5]`
  // WITHIN one block still collapse (iter 12 visual cluster intent).
  // indexOccurrence (iter 14 F1 sibling counter) is NOT reset because
  // sibling pairing crosses blocks by design.
  //
  // ADR-096 Phase 3 — cohort-gated reset. ADR-096 cohort bypasses the
  // consecutive-collapse predicate inside processText so the reset
  // becomes a no-op write; structurally cleaner to skip it entirely so
  // the closure-wide state mutation is visible ONLY when the legacy
  // contract actually consumes it. Phase 6 deletion target: when legacy
  // is removed, both lastRenderedCanonicalIdx + this reset disappear.
  function blockProcess(children: ReactNode): ReactNode {
    if (cohort !== "adr096") {
      lastRenderedCanonicalIdx = null;
    }
    return process(children);
  }

  return { process, blockProcess };
}

// react-markdown v10 contract: fenced code is wrapped in
// `<pre><code class="language-X">code\n</code></pre>` per remark-parse;
// inline backticks emit `<code>` with NO className. The `pre` override
// at the end of MessageRenderer's components map strips the `<pre>`
// wrapper for fenced so SyntaxCodeBlock can render at block level
// without nested `<pre><pre>`. Inline backticks never enter that path
// because remark-parse emits no surrounding `<pre>` for them.
//
// react-markdown v9+ REMOVED the legacy `inline` prop on the code
// override — DO NOT reintroduce that check. Pre-fix CodeBlock checked
// `if (inline)` which was always `undefined` under v10, so every
// backtick (inline + fenced alike) fell through to SyntaxCodeBlock.
// Operator-reported regression: short inline values like "60°C" /
// "1.5 bar" rendered as 122 full-block "CODE" panels in a single
// answer body — "badly formatted answer". The routing key under v10 is
// strictly className startsWith "language-".
function CodeBlock({ children, className }: { children?: ReactNode; className?: string }) {
  const isFenced = typeof className === "string" && className.startsWith("language-");
  if (!isFenced) {
    return (
      <code className="rounded bg-[var(--surface-sunken)] px-1 py-0.5 font-mono text-[0.85em] text-[var(--bk-fg)]">
        {children}
      </code>
    );
  }
  const code = extractText(children).replace(/\n$/, "");
  const language = extractLangFromClassName(className);
  return <SyntaxCodeBlock code={code} language={language} />;
}

// Workstream G2 — the rehype-injected `u-mark` element for an unverified-claim
// span. Rendered as a subtle MUTED DOTTED underline (distinct from links, which
// are solid + accent-coloured) with the claim text in a native hover tooltip;
// deliberately not alarming. `data-*` hast props arrive as kebab React props.
function UnverifiedMark(props: { children?: ReactNode; "data-claim"?: string; "data-facet"?: string }) {
  const claim = props["data-claim"];
  const title = typeof claim === "string" && claim.length > 0 ? `Unverified claim: ${claim}` : "Unverified claim";
  return (
    <span
      data-unverified-claim=""
      data-facet={props["data-facet"]}
      title={title}
      className="underline decoration-dotted decoration-1 decoration-[var(--text-tertiary)] underline-offset-[3px]"
    >
      {props.children}
    </span>
  );
}

// ADR-098 §3.4 attribution overrides. Same base styling as the legacy
// `components` map but renders children RAW — NO `[N]` processText, since
// P-Cite answers have no `[N]`; chips come from rehypeAttributionChips +
// the `cite-chip` adapter. Self-contained so the legacy path is untouched.
function buildAttributionComponents(
  chunkMap: Map<string, PersistedCitationRef>,
  sourceNumByChunkId: Map<string, number>,
) {
  // `cite-chip` is the rehype-injected element; data-* hast props arrive as
  // kebab React props (data-chunk-id). Wrapper span = stable QA selector,
  // keeps CitationChip untouched.
  function CiteChip(props: { "data-chunk-id"?: string }) {
    const chunkId = props["data-chunk-id"];
    if (typeof chunkId !== "string") return null;
    const cite = chunkMap.get(chunkId);
    if (!cite) return null;
    // v6.5: stable per-source number (matches the Sources strip); raw index is the fallback.
    const displayIdx = sourceNumByChunkId.get(chunkId) ?? cite.index;
    return (
      <span data-attribution-chip data-chunk-id={chunkId}>
        <CitationChip idx={cite.index} cite={cite} displayIdx={displayIdx} />
      </span>
    );
  }
  return {
    p: ({ children }: { children?: ReactNode }) => (
      <p className="type-body-lg my-[var(--space-4)] text-[var(--text-primary)] first:mt-0 last:mb-0">{children}</p>
    ),
    h1: ({ children }: { children?: ReactNode }) => (
      <h1 className="type-h2 mt-[var(--space-6)] mb-[var(--space-3)] text-[var(--text-primary)] first:mt-0">
        {children}
      </h1>
    ),
    h2: ({ children }: { children?: ReactNode }) => (
      <h2 className="type-h3 mt-[var(--space-5)] mb-[var(--space-2)] text-[var(--text-primary)] first:mt-0">
        {children}
      </h2>
    ),
    h3: ({ children }: { children?: ReactNode }) => (
      <h3 className="type-title mt-[var(--space-4)] mb-[var(--space-2)] text-[var(--text-primary)] first:mt-0">
        {children}
      </h3>
    ),
    ul: ({ children }: { children?: ReactNode }) => (
      <ul className="my-[var(--space-4)] ml-[var(--space-5)] list-disc space-y-[var(--space-2)]">{children}</ul>
    ),
    ol: ({ children }: { children?: ReactNode }) => (
      <ol className="my-[var(--space-4)] ml-[var(--space-5)] list-decimal space-y-[var(--space-2)]">{children}</ol>
    ),
    li: ({ children }: { children?: ReactNode }) => (
      <li className="type-body-lg text-[var(--text-primary)] marker:text-[var(--text-tertiary)]">{children}</li>
    ),
    a: ({ href, children }: { href?: string; children?: ReactNode }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[var(--accent-text)] underline underline-offset-2 transition-[text-decoration-thickness] duration-[var(--dur-fast)] hover:decoration-2"
      >
        {children}
      </a>
    ),
    table: ({ children }: { children?: ReactNode }) => (
      <div className="my-[var(--space-5)] overflow-x-auto rounded-[var(--radius-lg)] shadow-[var(--elev-ring)]">
        <table className="type-body-sm min-w-full border-collapse text-[var(--text-primary)]">{children}</table>
      </div>
    ),
    th: ({ children }: { children?: ReactNode }) => (
      <th className="type-body-sm border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-[var(--space-3)] py-[var(--space-2)] text-left font-semibold text-[var(--text-secondary)]">
        {children}
      </th>
    ),
    td: ({ children }: { children?: ReactNode }) => (
      <td className="type-body-sm border-b border-[var(--border-default)] px-[var(--space-3)] py-[var(--space-2)] text-[var(--text-primary)]">
        {children}
      </td>
    ),
    blockquote: ({ children }: { children?: ReactNode }) => (
      <blockquote className="type-body-lg my-[var(--space-4)] border-l-2 border-[var(--border-strong)] pl-[var(--space-4)] italic text-[var(--text-secondary)]">
        {children}
      </blockquote>
    ),
    strong: ({ children }: { children?: ReactNode }) => (
      <strong className="font-semibold text-[var(--text-primary)]">{children}</strong>
    ),
    em: ({ children }: { children?: ReactNode }) => <em>{children}</em>,
    hr: () => <hr className="my-[var(--space-6)] border-0 border-t border-[var(--border-default)]" />,
    img: SafeImage,
    code: CodeBlock,
    pre: ({ children }: { children?: ReactNode }) => <>{children}</>,
    "cite-chip": CiteChip,
    "u-mark": UnverifiedMark,
  };
}

export function MessageRenderer({
  content,
  citations,
  unverifiedClaims,
}: {
  content: string;
  citations: PersistedCitationRef[];
  unverifiedClaims?: UnverifiedClaimRef[];
}) {
  // ADR-096 Phase 3 — per-message cohort classification + dual-shape Map
  // build. Per-cite shape mixing within one m.citations from one synth
  // call is structurally impossible (single backend synth emission emits
  // ONE cohort) but cross-message mixing is real (sidebar replay of
  // legacy thread + fresh ADR-096 query in one session). The cohort
  // classifier at the useMemo boundary means each MessageRenderer
  // instance sees exactly one cohort + dispatches accordingly.
  //
  // ADR-096 path: 1:1 byMarkerIndex (no siblings, no canonical-idx remap
  // — marker_index IS canonical by construction). Legacy path: iter 14
  // F1 citationsByIndex<int, PersistedCitationRef[]> + iter 15 canonicalIdxByChunkId
  // (preserved verbatim). "mixed" cohort defensively falls back to legacy
  // construction — per ADR-096 §3.7 backward-compat shim, mixed should
  // never occur in production from a single synth call.
  const { cohort, byMarkerIndex, citationsByIndex, canonicalIdxByChunkId } = useMemo(() => {
    const cohortVal = classifyCitationCohort(citations);
    const byMarker = new Map<number, PersistedCitationRef>();
    const byIndex = new Map<number, PersistedCitationRef[]>();
    const canonicalIdx = new Map<string, number>();

    if (cohortVal === "adr096") {
      for (const c of citations) {
        if (typeof c.marker_index === "number") {
          byMarker.set(c.marker_index, c);
        }
      }
      return {
        cohort: cohortVal,
        byMarkerIndex: byMarker,
        citationsByIndex: byIndex,
        canonicalIdxByChunkId: canonicalIdx,
      };
    }

    // Iter 14 F1 — preserve ALL siblings per index. Pre-iter-14 the Map
    // kept only one cite per index (image-wins), silently dropping
    // siblings when backend emitted >1 PersistedCitationRef with same `index` but
    // different `chunk_id`. F1 stores list[] so processText's
    // per-occurrence counter resolves each nth `[N]` text occurrence to
    // its own sibling. Siblings sorted with image-bearing FIRST so the
    // single-occurrence case (1 `[N]` in text) preserves iter 11
    // image-wins behavior (siblings[0] = image-bearing). Multi-occurrence
    // case (`[5] ... [5]`) resolves first → siblings[0] (image), second
    // → siblings[1] (other) — image-wins applied to first, distinct
    // sibling for second.
    for (const c of citations) {
      const arr = byIndex.get(c.index) ?? [];
      arr.push(c);
      byIndex.set(c.index, arr);
    }
    for (const arr of byIndex.values()) {
      arr.sort((a, b) => {
        const aHasImg = typeof a.page_image_url === "string" && a.page_image_url.length > 0;
        const bHasImg = typeof b.page_image_url === "string" && b.page_image_url.length > 0;
        return Number(bHasImg) - Number(aHasImg);
      });
    }
    // chunk_id → LOWEST index (first-emitted = canonical) so every [N]
    // marker bound to one chunk renders as a single stable chip number.
    // This used to also align the chip with the Sources card; that card was
    // deleted 2026-08-09, so the remaining consumer is the chip itself.
    for (const c of citations) {
      const existing = canonicalIdx.get(c.chunk_id);
      if (existing === undefined || c.index < existing) {
        canonicalIdx.set(c.chunk_id, c.index);
      }
    }
    return {
      cohort: cohortVal,
      byMarkerIndex: byMarker,
      citationsByIndex: byIndex,
      canonicalIdxByChunkId: canonicalIdx,
    };
  }, [citations]);
  const components = useMemo(() => {
    const { process, blockProcess } = buildProcessChildren(
      cohort,
      byMarkerIndex,
      citationsByIndex,
      canonicalIdxByChunkId,
    );
    return {
      p: ({ children }: { children?: ReactNode }) => (
        <p className="type-body-lg my-[var(--space-4)] text-[var(--text-primary)] first:mt-0 last:mb-0">
          {blockProcess(children)}
        </p>
      ),
      h1: ({ children }: { children?: ReactNode }) => (
        <h1 className="type-h2 mt-[var(--space-6)] mb-[var(--space-3)] text-[var(--text-primary)] first:mt-0">
          {blockProcess(children)}
        </h1>
      ),
      h2: ({ children }: { children?: ReactNode }) => (
        <h2 className="type-h3 mt-[var(--space-5)] mb-[var(--space-2)] text-[var(--text-primary)] first:mt-0">
          {blockProcess(children)}
        </h2>
      ),
      h3: ({ children }: { children?: ReactNode }) => (
        <h3 className="type-title mt-[var(--space-4)] mb-[var(--space-2)] text-[var(--text-primary)] first:mt-0">
          {blockProcess(children)}
        </h3>
      ),
      ul: ({ children }: { children?: ReactNode }) => (
        <ul className="my-[var(--space-4)] ml-[var(--space-5)] list-disc space-y-[var(--space-2)]">{children}</ul>
      ),
      ol: ({ children }: { children?: ReactNode }) => (
        <ol className="my-[var(--space-4)] ml-[var(--space-5)] list-decimal space-y-[var(--space-2)]">{children}</ol>
      ),
      li: ({ children }: { children?: ReactNode }) => (
        <li className="type-body-lg text-[var(--text-primary)] marker:text-[var(--text-tertiary)]">
          {blockProcess(children)}
        </li>
      ),
      a: ({ href, children }: { href?: string; children?: ReactNode }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--accent-text)] underline underline-offset-2 transition-[text-decoration-thickness] duration-[var(--dur-fast)] hover:decoration-2"
        >
          {process(children)}
        </a>
      ),
      table: ({ children }: { children?: ReactNode }) => (
        <div className="my-[var(--space-5)] overflow-x-auto rounded-[var(--radius-lg)] shadow-[var(--elev-ring)]">
          <table className="type-body-sm min-w-full border-collapse text-[var(--text-primary)]">{children}</table>
        </div>
      ),
      th: ({ children }: { children?: ReactNode }) => (
        <th className="type-body-sm border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-[var(--space-3)] py-[var(--space-2)] text-left font-semibold text-[var(--text-secondary)]">
          {blockProcess(children)}
        </th>
      ),
      td: ({ children }: { children?: ReactNode }) => (
        <td className="type-body-sm border-b border-[var(--border-default)] px-[var(--space-3)] py-[var(--space-2)] text-[var(--text-primary)]">
          {blockProcess(children)}
        </td>
      ),
      blockquote: ({ children }: { children?: ReactNode }) => (
        <blockquote className="type-body-lg my-[var(--space-4)] border-l-2 border-[var(--border-strong)] pl-[var(--space-4)] italic text-[var(--text-secondary)]">
          {children}
        </blockquote>
      ),
      strong: ({ children }: { children?: ReactNode }) => (
        <strong className="font-semibold text-[var(--text-primary)]">{process(children)}</strong>
      ),
      em: ({ children }: { children?: ReactNode }) => <em>{process(children)}</em>,
      hr: () => <hr className="my-[var(--space-6)] border-0 border-t border-[var(--border-default)]" />,
      img: SafeImage,
      code: CodeBlock,
      pre: ({ children }: { children?: ReactNode }) => <>{children}</>,
      "u-mark": UnverifiedMark,
    };
  }, [cohort, byMarkerIndex, citationsByIndex, canonicalIdxByChunkId]);

  // Keyed on [content, citations]: while streaming, a span whose
  // answer_span_end hasn't streamed yet yields null from codePointToUtf16
  // and is DROPPED — the chip reappears once `content` catches up.
  const attributionSpans = useMemo<AttributionSpan[]>(() => {
    const out: AttributionSpan[] = [];
    for (const cite of citations) {
      if (!isAttributionCitation(cite)) continue;
      const end = cite.answer_span_end;
      if (typeof end !== "number") continue;
      const utf16End = codePointToUtf16(content, end);
      if (utf16End === null) continue;
      out.push({ utf16End, chunkId: cite.chunk_id, index: cite.index });
    }
    return out;
  }, [content, citations]);

  // Workstream G2 — unverified-claim ranges mapped to render (utf16) coordinates.
  // Empty/undefined (default-OFF flag) → no marker, byte-inert render.
  const unverifiedSpans = useMemo<UnverifiedSpan[]>(
    () => buildUnverifiedSpans(content, unverifiedClaims ?? []),
    [content, unverifiedClaims],
  );

  // ADR-099 follow-up — Architecture D: placement happens in the STRING domain
  // BEFORE parse (sentinels injected here, split by rehypeAttributionChips),
  // NOT by matching offsets onto the parsed tree — that is what anchors chips
  // at structural boundaries (bullets/tables/headings). insertClaimSentinels
  // co-injects the unverified OPEN/CLOSE pair in the SAME coordinate space so
  // citation + unverified markers never shift each other's offsets.
  const sentinelContent = useMemo(
    () => insertClaimSentinels(content, attributionSpans, unverifiedSpans),
    [content, attributionSpans, unverifiedSpans],
  );

  // Non-attribution cohorts carry no citation sentinels; inject only the
  // unverified pair (returns `content` byte-identical when there are none).
  const legacyContent = useMemo(() => insertClaimSentinels(content, [], unverifiedSpans), [content, unverifiedSpans]);

  const attributionChunkMap = useMemo(() => {
    const map = new Map<string, PersistedCitationRef>();
    for (const cite of citations) map.set(cite.chunk_id, cite);
    return map;
  }, [citations]);

  const attributionSourceNum = useMemo(() => buildSourceNumberByChunkId(citations), [citations]);

  const attributionComponents = useMemo(
    () => buildAttributionComponents(attributionChunkMap, attributionSourceNum),
    [attributionChunkMap, attributionSourceNum],
  );

  const unverifiedPlugin: RehypePluggable[] =
    unverifiedSpans.length > 0 ? [[rehypeUnverifiedMarks, { spans: unverifiedSpans }] as RehypePluggable] : [];

  if (cohort === "attribution") {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeAttributionChips, { spans: attributionSpans }] as RehypePluggable, ...unverifiedPlugin]}
        components={attributionComponents as Components}
      >
        {sentinelContent}
      </ReactMarkdown>
    );
  }

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={unverifiedPlugin} components={components}>
      {legacyContent}
    </ReactMarkdown>
  );
}
